// reduced circles on line chart
import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import styles from './HolderAnalyticsDetailed.module.sass';
import { getDetailedHistoricHolders } from 'services/portfolio';
import Spinner from 'components/Spinner';

const HolderAnalyticsDetailed = ({ accessToken, contractAddress, chain = "eth" }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('5d');
  const [timeframe, setTimeframe] = useState('1day');
  const [holderData, setHolderData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [holderFlowType, setHolderFlowType] = useState('incoming'); // Toggle for incoming/outgoing
  const [hoveredType, setHoveredType] = useState(null); // Add this state variable for hover functionality
  
  // Function to format dates consistently (always using local time)
  const formatDate = (timestamp) => {
    // Create a Date object from the timestamp
    const date = new Date(timestamp);
    
    // Format using local time
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    return `${month} ${day}`;
  };
  
  // Function to get tooltip timestamp with time component (always using local time)
  const getTooltipTime = (timestamp) => {
    const date = new Date(timestamp);
    // Format using local time with hours and minutes
    return date.toLocaleString();
  };
  
  // Function to calculate the fromDate based on dateRange
  const calculateFromDate = (range) => {
    // Create a date in UTC
    const now = new Date();
    // Convert to UTC date string (YYYY-MM-DDT00:00:00.000Z)
    const utcNow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString();
    // Create a date object from the UTC string
    const date = new Date(utcNow);
    
    switch (range) {
      case '1d':
        date.setUTCDate(date.getUTCDate() - 1);
        break;
      case '3d':
        date.setUTCDate(date.getUTCDate() - 3);
        break;
      case '5d':
        date.setUTCDate(date.getUTCDate() - 5);
        break;
      case '7d':
        date.setUTCDate(date.getUTCDate() - 7);
        break;
      case '14d':
        date.setUTCDate(date.getUTCDate() - 14);
        break;
      case '30d':
        date.setUTCDate(date.getUTCDate() - 30);
        break;
      case '60d':
        date.setUTCDate(date.getUTCDate() - 60);
        break;
      default:
        date.setUTCDate(date.getUTCDate() - 5);
    }
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  // Convert timeframe to API interval parameter
  const convertTimeframeToInterval = (tf) => {
    const mapping = {
      '1min': '1min',
      '5min': '5min',
      '10min': '10min',
      '30min': '30min',
      '1hour': '1h',
      '4hour': '4h',
      '12hour': '12h',
      '1day': '1d',
      '1week': '1w',
      '1month': '1M'
    };
    return mapping[tf] || '1d';
  };
  
  // Fetch data function
  const fetchHolderData = async () => {
    if (!accessToken || !contractAddress) {
      console.warn('Missing required parameters: accessToken or contractAddress');
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const fromDate = calculateFromDate(dateRange);
      const interval = convertTimeframeToInterval(timeframe);
      
      const response = await getDetailedHistoricHolders(
        accessToken,
        contractAddress,
        fromDate,
        chain,
        interval
      );
      
      if (response && response.result && Array.isArray(response.result)) {
        setHolderData(response.result.reverse()); // Reverse to show oldest to newest
      } else {
        setError('Invalid data format received from API');
        console.error('Invalid data format');
      }
    } catch (err) {
      setError('Failed to fetch holder data');
      console.error('Error fetching holder data');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle date range change
  const handleRangeChange = (e) => {
    setDateRange(e.target.value);
  };
  
  // Handle timeframe change
  const handleTimeframeChange = (e) => {
    setTimeframe(e.target.value);
  };

  // Apply button handler
  const handleApply = () => {
    fetchHolderData();
  };
  
  // Initial data fetch on component mount
  useEffect(() => {
    fetchHolderData();
  }, [accessToken, contractAddress, chain]); // Re-fetch when these props change
  
  if (isLoading) {
    return <Spinner />;
  }

  // If there was an error, show error state
  if (error) {
    return (
      <div className={styles.holderAnalyticsPanel}>
        <div className={styles.errorState}>
          <div className={styles.errorMessage}>{error}</div>
          <button onClick={fetchHolderData} className={styles.retryButton}>Retry</button>
        </div>
      </div>
    );
  }

  // If no data is available after loading, show empty state
  if (holderData.length === 0) {
    return (
      <div className={styles.holderAnalyticsPanel}>
        <div className={styles.emptyState}>
          <div className={styles.emptyMessage}>No holder data available</div>
          <button onClick={fetchHolderData} className={styles.retryButton}>Refresh</button>
        </div>
      </div>
    );
  }
  
  // Transform data for acquisition chart with both formatted date and tooltip time
  const acquisitionData = holderData.map(day => ({
    date: formatDate(day.timestamp),
    tooltipTime: getTooltipTime(day.timestamp),
    originalTimestamp: day.timestamp, // Keep original timestamp
    swap: day.newHoldersByAcquisition.swap,
    transfer: day.newHoldersByAcquisition.transfer,
    airdrop: day.newHoldersByAcquisition.airdrop,
  }));
  
  // Transform data for holder types chart
  const holderTypes = holderData.map(day => {
    // Choose between incoming and outgoing data based on toggle
    const holderSet = holderFlowType === 'incoming' ? day.holdersIn : day.holdersOut;
    
    return {
      date: formatDate(day.timestamp),
      tooltipTime: getTooltipTime(day.timestamp),
      originalTimestamp: day.timestamp, // Keep original timestamp
      whales: holderSet.whales,
      sharks: holderSet.sharks,
      dolphins: holderSet.dolphins,
      fish: holderSet.fish,
      octopus: holderSet.octopus,
      crab: holderSet.crab,
      shrimps: holderSet.shrimps,
    };
  });
  
  // Transform data for holder flow
  const holderFlowData = holderData.map(day => {
    const totalIn = Object.values(day.holdersIn).reduce((sum, val) => sum + val, 0);
    const totalOut = Object.values(day.holdersOut).reduce((sum, val) => sum + val, 0);
    
    return {
      date: formatDate(day.timestamp),
      tooltipTime: getTooltipTime(day.timestamp),
      originalTimestamp: day.timestamp, // Keep original timestamp
      'Holders In': totalIn,
      'Holders Out': totalOut,
      'Net Change': day.netHolderChange
    };
  });
  
  // Calculate totals for summary metrics
  const latestDay = holderData[holderData.length - 1];
  const totalHolders = latestDay.totalHolders;
  const netChange = holderData.reduce((sum, day) => sum + day.netHolderChange, 0);
  const percentChange = (netChange / (totalHolders - netChange) * 100).toFixed(2);
  
  // Calculate daily average
  const dailyAverage = Math.round(netChange / holderData.length);
  
  // Calculate acquisition breakdown for summary metrics
  const totalSwap = holderData.reduce((sum, day) => sum + day.newHoldersByAcquisition.swap, 0);
  const totalTransfer = holderData.reduce((sum, day) => sum + day.newHoldersByAcquisition.transfer, 0);
  const totalAirdrop = holderData.reduce((sum, day) => sum + day.newHoldersByAcquisition.airdrop, 0);
  
  // Custom tooltip component to display formatted time
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={styles.tooltip} style={{ 
          backgroundColor: 'rgba(23, 23, 30, 0.95)', 
          borderColor: 'rgba(115, 246, 255, 0.2)',
          color: '#73F6FF',
          padding: '10px',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          <p className={styles.tooltipTime} style={{ color: '#73F6FF', fontWeight: 600, marginBottom: '5px' }}>
            {payload[0].payload.tooltipTime || label}
          </p>
          {payload.map((entry, index) => (
            <p key={`item-${index}`} style={{ color: entry.color, margin: '2px 0' }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className={styles.holderAnalyticsPanel}>
      <div className={styles.chartSection}>
        <div className={styles.compactHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.dateRangeSelector}>
              <div className={styles.selectGroup}>
                <div className={styles.selectLabel}>Timeframe</div>
                <select 
                  value={dateRange} 
                  onChange={handleRangeChange}
                  className={styles.dateSelect}
                >
                  <option value="1d">1 Day</option>
                  <option value="3d">3 Days</option>
                  <option value="5d">5 Days</option>
                  <option value="7d">7 Days</option>
                  <option value="14d">14 Days</option>
                  <option value="30d">30 Days</option>
                  <option value="60d">60 Days</option>
                </select>
              </div>
              
              <div className={styles.selectGroup}>
                <div className={styles.selectLabel}>Interval</div>
                <select
                  className={styles.dateSelect}
                  value={timeframe}
                  onChange={handleTimeframeChange}
                >
                  <option value="1min">1 Minute</option>
                  <option value="5min">5 Minutes</option>
                  <option value="10min">10 Minutes</option>
                  <option value="30min">30 Minutes</option>
                  <option value="1hour">1 Hour</option>
                  <option value="4hour">4 Hours</option>
                  <option value="12hour">12 Hours</option>
                  <option value="1day">1 Day</option>
                  <option value="1week">1 Week</option>
                  <option value="1month">1 Month</option>
                </select>
              </div>
              
              <button className={styles.applyButton} onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>
          
          <div className={styles.tabNavigation}>
            <button 
              className={`${styles.tabButton} ${activeTab === 'overview' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'acquisition' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('acquisition')}
            >
              Acquisition
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'holderTypes' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('holderTypes')}
            >
              Holder Categories
            </button>
          </div>
        </div>
        
        <div className={styles.analyticsContent}>
          <div className={styles.metricCards}>
            <div className={styles.metricCard}>
              <div className={styles.metricTitle}>Total Holders</div>
              <div className={styles.metricValue}>{totalHolders.toLocaleString()}</div>
            </div>
            
            <div className={styles.metricCard}>
              <div className={styles.metricTitle}>Net Change ({dateRange})</div>
              <div className={`${styles.metricValue} ${netChange >= 0 ? styles.positive : styles.negative}`}>
                {netChange >= 0 ? '+' : ''}{netChange.toLocaleString()} 
                <span className={styles.metricSubtext}>({percentChange}%)</span>
              </div>
            </div>
            
            <div className={styles.metricCard}>
              <div className={styles.metricTitle}>Average</div>
              <div className={`${styles.metricValue} ${dailyAverage > 0 ? styles.positive : dailyAverage < 0 ? styles.negative : ''}`}>
                {dailyAverage > 0 ? '+' : dailyAverage < 0 ? '' : ''}{dailyAverage.toLocaleString()}
                <span className={styles.metricSubtext}></span>
              </div>
            </div>
            
            <div className={styles.metricCard}>
              <div className={styles.metricTitle}>
                Retention Rate
                <div className={styles.infoIcon} data-tooltip="Retention rates vary by timeframe: shorter intervals show more volatility from trading activity, while longer intervals smooth fluctuations and better reflect long-term holder behavior.">ⓘ</div>
              </div>
              <div className={styles.metricValue}>
                {(100 - (holderData.reduce((sum, day) => sum + Object.values(day.holdersOut).reduce((s, v) => s + v, 0), 0) / 
                 holderData.reduce((sum, day) => sum + Object.values(day.holdersIn).reduce((s, v) => s + v, 0), 0) * 100)).toFixed(1)}%
                <span className={styles.metricSubtext}>avg</span>
              </div>
            </div>
          </div>
          
          <div className={styles.chartWrapper}>
            <div className={styles.chartTitleWithToggle}>
              {activeTab === 'overview' && (
                <span className={styles.chartTitle}>Holder Growth & Flow</span>
              )}
              {activeTab === 'acquisition' && (
                <span className={styles.chartTitle}>New Holders by Acquisition Method</span>
              )}
              {activeTab === 'holderTypes' && (
                <>
                  <span className={styles.chartTitle}>
                    Holder Categories
                  </span>
                  <div className={styles.toggleButtons}>
                    <button 
                      className={`${styles.toggleButton} ${holderFlowType === 'incoming' ? styles.active : ''}`} 
                      onClick={() => setHolderFlowType('incoming')}
                    >
                      Incoming
                    </button>
                    <button 
                      className={`${styles.toggleButton} ${holderFlowType === 'outgoing' ? styles.active : ''}`} 
                      onClick={() => setHolderFlowType('outgoing')}
                    >
                      Outgoing
                    </button>
                  </div>
                </>
              )}
            </div>
            
            <div className={styles.chartContainer}>
              <ResponsiveContainer width="100%" height={250}>
                {activeTab === 'overview' && (
                  <BarChart data={holderFlowData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(115, 246, 255, 0.1)" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: 'rgba(115, 246, 255, 0.6)', fontSize: 12 }}
                      axisLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                      tickLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                    />
                    <YAxis 
                      tick={{ fill: 'rgba(115, 246, 255, 0.6)', fontSize: 12 }}
                      axisLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                      tickLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                    />
                    <Legend wrapperStyle={{ color: 'rgba(115, 246, 255, 0.6)' }} />
                    <Bar dataKey="Holders In" fill="#0ECDF7" name="Holders In" />
                    <Bar dataKey="Holders Out" fill="#98A4FF" name="Holders Out" />
                    <Line 
                      type="monotone" 
                      dataKey="Net Change" 
                      stroke="#73F6FF" 
                      strokeWidth={2} 
                      dot={false}
                      activeDot={false}
                    />
                  </BarChart>
                )}
                
                {activeTab === 'acquisition' && (
                  <LineChart data={acquisitionData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(115, 246, 255, 0.1)" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: 'rgba(115, 246, 255, 0.6)', fontSize: 12 }}
                      axisLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                      tickLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                    />
                    <YAxis 
                      tick={{ fill: 'rgba(115, 246, 255, 0.6)', fontSize: 12 }}
                      axisLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                      tickLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                    />
                    <Legend wrapperStyle={{ color: 'rgba(115, 246, 255, 0.6)' }} />
                    <Line 
                      type="monotone" 
                      dataKey="swap" 
                      stroke="#0ECDF7" 
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                      name="Swap" 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="transfer" 
                      stroke="#4098FF" 
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                      name="Transfer" 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="airdrop" 
                      stroke="#73F6FF" 
                      strokeWidth={2}
                      dot={false}
                      activeDot={false}
                      name="Airdrop" 
                    />
                  </LineChart>
                )}
                
                {activeTab === 'holderTypes' && (
                  <BarChart data={holderTypes} barSize={20} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(115, 246, 255, 0.1)" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: 'rgba(115, 246, 255, 0.6)', fontSize: 12 }}
                      axisLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                      tickLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                    />
                    <YAxis 
                      tick={{ fill: 'rgba(115, 246, 255, 0.6)', fontSize: 12 }}
                      axisLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                      tickLine={{ stroke: 'rgba(115, 246, 255, 0.1)' }}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                    />
                    <Legend wrapperStyle={{ color: 'rgba(115, 246, 255, 0.6)' }} />
                    <Bar dataKey="whales" stackId="a" fill="#0077B6" name="Whales" />
                    <Bar dataKey="sharks" stackId="a" fill="#0096C7" name="Sharks" />
                    <Bar dataKey="dolphins" stackId="a" fill="#00B4D8" name="Dolphins" />
                    <Bar dataKey="fish" stackId="a" fill="#48CAE4" name="Fish" />
                    <Bar dataKey="octopus" stackId="a" fill="#90E0EF" name="Octopus" />
                    <Bar dataKey="crab" stackId="a" fill="#ADE8F4" name="Crabs" />
                    <Bar dataKey="shrimps" stackId="a" fill="#73F6FF" name="Shrimps" />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
          
          <div className={styles.bottomMetrics}>
            <div className={styles.metricGroup}>
              <div className={styles.metricLabel}>Acquisition Breakdown</div>
              <div className={styles.barBreakdown}>
                <div className={styles.barItem}>
                  <div className={styles.barLabel}>Swap</div>
                  <div className={styles.barContainer}>
                    <div 
                      className={styles.barFill} 
                      style={{ 
                        width: `${(totalSwap / (totalSwap + totalTransfer + totalAirdrop)) * 100}%`,
                        background: '#0ECDF7'
                      }}
                    ></div>
                  </div>
                  <div className={styles.barValue}>{totalSwap}</div>
                </div>
                <div className={styles.barItem}>
                  <div className={styles.barLabel}>Transfer</div>
                  <div className={styles.barContainer}>
                    <div 
                      className={styles.barFill} 
                      style={{ 
                        width: `${(totalTransfer / (totalSwap + totalTransfer + totalAirdrop)) * 100}%`,
                        background: '#4098FF'
                      }}
                    ></div>
                  </div>
                  <div className={styles.barValue}>{totalTransfer}</div>
                </div>
                <div className={styles.barItem}>
                  <div className={styles.barLabel}>Airdrop</div>
                  <div className={styles.barContainer}>
                    <div 
                      className={styles.barFill} 
                      style={{ 
                        width: `${(totalAirdrop / (totalSwap + totalTransfer + totalAirdrop)) * 100}%`,
                        background: '#98A4FF'
                      }}
                    ></div>
                  </div>
                  <div className={styles.barValue}>{totalAirdrop}</div>
                </div>
              </div>
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Total:</span>
                <span className={styles.totalValue}>{totalSwap + totalTransfer + totalAirdrop}</span>
              </div>
            </div>
            
            <div className={styles.metricGroup}>
              <div className={styles.metricLabelWithToggle}>
                <span className={styles.metricLabel}>
                  Holder Distribution
                </span>
                <div className={styles.toggleButtons}>
                  <button 
                    className={`${styles.smallToggleButton} ${holderFlowType === 'incoming' ? styles.active : ''}`}
                    onClick={() => setHolderFlowType('incoming')}
                  >
                    Incoming
                  </button>
                  <button 
                    className={`${styles.smallToggleButton} ${holderFlowType === 'outgoing' ? styles.active : ''}`}
                    onClick={() => setHolderFlowType('outgoing')}
                  >
                    Outgoing
                  </button>
                </div>
              </div>
              <div className={styles.histogram}>
                {Object.keys(holderData[0].holdersIn).map((type) => {
                  // Use the corresponding data based on toggle state
                  const holderSet = holderFlowType === 'incoming' ? 'holdersIn' : 'holdersOut';
                  const count = holderData.reduce((sum, day) => sum + day[holderSet][type], 0);
                  const total = holderData.reduce((sum, day) => 
                    sum + Object.values(day[holderSet]).reduce((s, v) => s + v, 0), 0);
                  const percentage = total > 0 ? (count / total * 100) : 0;
                  
                  return (
                    <div key={type} className={styles.histogramBar}>
                      <div className={styles.histogramLabel}>{type}</div>
                      <div className={styles.histogramBarWrapper}>
                        <div 
                          className={styles.histogramBarFill}
                          style={{ width: `${Math.max(percentage, 0.5)}%` }}
                          onMouseEnter={() => setHoveredType(type)}
                          onMouseLeave={() => setHoveredType(null)}
                        >
                          {hoveredType === type && (
                            <div className={styles.hoverCount}>
                              {count.toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={styles.histogramValue}>
                        {percentage < 1 ? '<1%' : `${Math.round(percentage)}%`}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className={styles.totalRow}>
                <span className={styles.totalLabel}>Total {holderFlowType === 'incoming' ? 'In' : 'Out'}:</span>
                <span className={styles.totalValue}>
                  {holderData.reduce((sum, day) => 
                    sum + Object.values(holderFlowType === 'incoming' ? day.holdersIn : day.holdersOut)
                      .reduce((s, v) => s + v, 0), 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HolderAnalyticsDetailed;